/**
 * Public API Settings
 *
 * Tenant-level configuration for the public API.
 */

/** Available API versions */
export type PublicApiVersion = '2026-01';

/** All supported API versions */
export const SUPPORTED_PUBLIC_API_VERSIONS: PublicApiVersion[] = ['2026-01'];

/** Default API version when not configured */
export const DEFAULT_PUBLIC_API_VERSION: PublicApiVersion = '2026-01';

/** Platform default rate limits (per OAuth application) */
export const DEFAULT_PUBLIC_API_DAILY_LIMIT = 5000;
export const DEFAULT_PUBLIC_API_BURST_LIMIT = 60;

/** Sentinel used at enforce-time when a tier is Unlimited */
export const PUBLIC_API_RATE_LIMIT_UNLIMITED_VALUE = Number.MAX_SAFE_INTEGER;

/**
 * Discrete daily limit tiers (null = unlimited).
 * Exponential-ish steps for SysAdmin overrides — no free-form numbers.
 */
export const PUBLIC_API_DAILY_LIMIT_TIERS: ReadonlyArray<number | null> = [
    5_000,
    10_000,
    25_000,
    50_000,
    100_000,
    null,
];

/**
 * Discrete burst (per-minute) limit tiers (null = unlimited).
 */
export const PUBLIC_API_BURST_LIMIT_TIERS: ReadonlyArray<number | null> = [
    60,
    120,
    300,
    600,
    1_000,
    null,
];

/** Default tier selection when enabling a new override (one step above platform default) */
export const DEFAULT_OVERRIDE_DAILY_LIMIT = 10_000;
export const DEFAULT_OVERRIDE_BURST_LIMIT = 120;

/**
 * Lifespan for a tenant-wide public API rate limit override.
 * Mirrors IP restriction UX — pick a duration, server stores expiresAt.
 */
export enum PublicApiRateLimitLifespan {
    Permanent = 'permanent',
    OneHour = '1h',
    EightHours = '8h',
    TwentyFourHours = '24h',
    SevenDays = '7d',
    ThirtyDays = '30d',
}

/**
 * Tenant-wide rate limit override (System Administrator only).
 * dailyLimit / burstLimit: tier number, or null for unlimited.
 */
export interface PublicApiRateLimitOverride {
    dailyLimit: number | null;
    burstLimit: number | null;
    lifespan: PublicApiRateLimitLifespan;
    /** Computed server-side when saved; null/undefined when permanent */
    expiresAt?: string | null;
    reason?: string;
}

/** Public API tenant settings */
export interface PublicApiSettings {
    /** Default API version for this tenant */
    defaultVersion: PublicApiVersion;
    /** Optional SysAdmin rate limit override for all OAuth applications */
    rateLimit?: PublicApiRateLimitOverride | null;
    /** Monotonic revision for optimistic concurrency on SysAdmin writes */
    revision?: number;
}

/** Default settings when not configured in database */
export const DEFAULT_PUBLIC_API_SETTINGS: PublicApiSettings = {
    defaultVersion: DEFAULT_PUBLIC_API_VERSION,
    revision: 0,
};

/** Dashboard / command payload for Public API settings (version + rate limits) */
export interface PublicApiSettingsState {
    defaultVersion: PublicApiVersion;
    supportedVersions: ReadonlyArray<PublicApiVersion>;
    /** Server revision — send back as expectedRevision on writes */
    revision: number;
    defaults: {
        dailyLimit: number;
        burstLimit: number;
    };
    dailyTiers: ReadonlyArray<number | null>;
    burstTiers: ReadonlyArray<number | null>;
    override: PublicApiRateLimitOverride | null;
}

/** @deprecated Use PublicApiSettingsState */
export type PublicApiRateLimitState = PublicApiSettingsState;

/** Set override request (lifespan → expiresAt computed server-side) */
export interface SetPublicApiRateLimitRequest {
    dailyLimit: number | null;
    burstLimit: number | null;
    lifespan: PublicApiRateLimitLifespan;
    reason?: string;
    /** Required for optimistic concurrency — from last Get/Set response */
    expectedRevision: number;
}

/** Set tenant default API version (when client omits X-WorkBuddy-Version) */
export interface SetPublicApiDefaultVersionRequest {
    defaultVersion: PublicApiVersion;
    expectedRevision: number;
}

/** Clear rate limit override */
export interface ClearPublicApiRateLimitRequest {
    expectedRevision: number;
}

/** Check if a version string is valid */
export function isValidPublicApiVersion(version: string): version is PublicApiVersion {

    return SUPPORTED_PUBLIC_API_VERSIONS.includes(version as PublicApiVersion);
}

export function isValidPublicApiRateLimitLifespan(value: string): value is PublicApiRateLimitLifespan {

    return Object.values(PublicApiRateLimitLifespan).includes(value as PublicApiRateLimitLifespan);
}

export function isValidPublicApiDailyLimitTier(value: number | null | undefined): boolean {

    return PUBLIC_API_DAILY_LIMIT_TIERS.some(tier => tier === value);
}

export function isValidPublicApiBurstLimitTier(value: number | null | undefined): boolean {

    return PUBLIC_API_BURST_LIMIT_TIERS.some(tier => tier === value);
}

export function formatPublicApiRateLimitTier(value: number | null | undefined): string {

    if (value == null) return 'Unlimited';
    return value.toLocaleString('en-US');
}

/** Milliseconds for a lifespan, or null when permanent */
export function publicApiRateLimitLifespanMs(lifespan: PublicApiRateLimitLifespan): number | null {

    switch (lifespan) {
        case PublicApiRateLimitLifespan.Permanent:
            return null;
        case PublicApiRateLimitLifespan.OneHour:
            return 60 * 60 * 1000;
        case PublicApiRateLimitLifespan.EightHours:
            return 8 * 60 * 60 * 1000;
        case PublicApiRateLimitLifespan.TwentyFourHours:
            return 24 * 60 * 60 * 1000;
        case PublicApiRateLimitLifespan.SevenDays:
            return 7 * 24 * 60 * 60 * 1000;
        case PublicApiRateLimitLifespan.ThirtyDays:
            return 30 * 24 * 60 * 60 * 1000;
        default:
            return null;
    }
}

export function computePublicApiRateLimitExpiresAt(lifespan: PublicApiRateLimitLifespan, from: Date = new Date()): string | null {

    const ms = publicApiRateLimitLifespanMs(lifespan);
    if (ms == null) return null;
    return new Date(from.getTime() + ms).toISOString();
}

/** True when override exists and is not past expiresAt */
export function isPublicApiRateLimitOverrideActive(override: PublicApiRateLimitOverride | null | undefined, now: Date = new Date()): boolean {

    if (!override) return false;
    if (!override.expiresAt) return true;
    const expires = new Date(override.expiresAt);
    if (Number.isNaN(expires.getTime())) return false;
    return expires.getTime() > now.getTime();
}

/** Map stored tier (null = unlimited) to a numeric cap for enforcement */
export function toEnforcedPublicApiLimit(tier: number | null | undefined, fallback: number): number {

    if (tier === null) return PUBLIC_API_RATE_LIMIT_UNLIMITED_VALUE;
    if (typeof tier === 'number' && Number.isFinite(tier) && tier > 0) return tier;
    return fallback;
}

export function resolvePublicApiRateLimits(settings: PublicApiSettings | null | undefined, now: Date = new Date()): { dailyLimit: number; burstLimit: number } {

    const override = settings?.rateLimit;
    if (isPublicApiRateLimitOverrideActive(override, now)) {
        return {
            dailyLimit: toEnforcedPublicApiLimit(override.dailyLimit, DEFAULT_PUBLIC_API_DAILY_LIMIT),
            burstLimit: toEnforcedPublicApiLimit(override.burstLimit, DEFAULT_PUBLIC_API_BURST_LIMIT),
        };
    }

    return {
        dailyLimit: DEFAULT_PUBLIC_API_DAILY_LIMIT,
        burstLimit: DEFAULT_PUBLIC_API_BURST_LIMIT,
    };
}

export function normalizePublicApiSettings(value: PublicApiSettings | null | undefined): PublicApiSettings {

    const revision = typeof value?.revision === 'number' && Number.isFinite(value.revision) && value.revision >= 0
        ? Math.trunc(value.revision)
        : 0;

    return {
        defaultVersion: value?.defaultVersion && isValidPublicApiVersion(value.defaultVersion)
            ? value.defaultVersion
            : DEFAULT_PUBLIC_API_VERSION,
        rateLimit: value?.rateLimit ?? null,
        revision,
    };
}
